import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Plus, Trash2, Save, Image as ImageIcon, MoveUp, MoveDown, X, Upload, Sparkles,
} from "lucide-react";

/**
 * GuideEditor — panneau admin qui laisse ajouter des blocs personnalisés
 * (photo, encart, mini-galerie) à n'importe quel chapitre du Guide.
 *
 * Persistance : les blocs sont stockés dans `content.guideExtras[chapter]`.
 * L'API existante `PUT /admin/content` gère l'écriture (validation + history
 * fournis par le backend).
 */

// Liste (fixe) des chapitres du Guide — miroir de /content/guide.js
const CHAPTERS = [
  { slug: "adresse",       label: "L'adresse" },
  { slug: "code-acces",    label: "Le code d'accès" },
  { slug: "transport",     label: "Transport" },
  { slug: "wifi",          label: "Wifi" },
  { slug: "bassin",        label: "Le bassin" },
  { slug: "eclairage",     label: "Éclairage & son" },
  { slug: "equipements",   label: "Équipements" },
  { slug: "cuisine",       label: "La cuisine" },
  { slug: "climatisation", label: "Climatisation" },
  { slug: "regles",        label: "Règles de la maison" },
  { slug: "urgences",      label: "Urgences" },
  { slug: "contact",       label: "Contact & réseaux" },
  { slug: "depart",        label: "Refermer la porte" },
];

// Types de blocs disponibles à l'ajout
const BLOCK_TYPES = [
  {
    type: "photo-caption",
    label: "Photo avec légende",
    hint: "Une belle image + une légende courte",
    defaults: { type: "photo-caption", src: "", alt: "", caption: "", subCaption: "" },
  },
  {
    type: "callout",
    label: "Encart (icône + texte)",
    hint: "Un cadre avec icône, titre en majuscules et texte — image facultative",
    defaults: { type: "callout", icon: "Sparkles", label: "", value: "", image: "", imageAlt: "" },
  },
  {
    type: "gallery",
    label: "Mini-galerie (2 à 6 photos)",
    hint: "Grille responsive de photos avec légendes optionnelles",
    defaults: { type: "gallery", eyebrow: "", heading: "", photos: [] },
  },
];

const ICON_CHOICES = [
  "Sparkles", "MapPin", "KeyRound", "Wifi", "Wind", "Sun", "Droplets", "Flower2",
  "Clock", "Timer", "Recycle", "MessageCircle", "Flame", "Phone", "Mail",
  "UserCheck", "Music", "Film", "PlayCircle", "Instagram",
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GuideEditor({ site }) {
  const [content, setContent] = useState({});
  const [chapter, setChapter] = useState("adresse");
  const [uploaded, setUploaded] = useState([]);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // { path, callback }

  const load = async () => {
    const [c, m] = await Promise.all([
      api.getContent(site).catch(() => ({})),
      api.listMedia(site).catch(() => []),
    ]);
    setContent(c || {});
    setUploaded(m || []);
  };
  useEffect(() => { load(); }, [site]);

  const extras = useMemo(
    () => (content?.guideExtras?.[chapter] || []),
    [content, chapter]
  );

  const setExtras = (next) => {
    const merged = {
      ...content,
      guideExtras: { ...(content.guideExtras || {}), [chapter]: next },
    };
    setContent(merged);
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.putContent(site, content);
      setMsg("✓ Enregistré");
      setTimeout(() => setMsg(""), 2200);
    } catch (e) {
      setMsg("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (blockDef) => {
    setExtras([...extras, JSON.parse(JSON.stringify(blockDef.defaults))]);
  };

  const removeBlock = (idx) => {
    if (!window.confirm("Supprimer ce bloc ?")) return;
    setExtras(extras.filter((_, i) => i !== idx));
  };

  const moveBlock = (idx, delta) => {
    const next = [...extras];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setExtras(next);
  };

  const updateBlock = (idx, patch) => {
    setExtras(extras.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  // Ouvre le picker de médias pour un champ donné
  const openPicker = (onPick) => setPickerFor({ onPick });
  const closePicker = () => setPickerFor(null);

  return (
    <div data-testid="guide-editor">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-light text-refuge-text mb-1">
            Guide du Voyageur <span className="text-refuge-soft text-lg">· blocs personnalisés</span>
          </h2>
          <p className="font-body text-xs text-refuge-soft italic">
            Ajoute des photos, encarts et mini-galeries à la fin de chaque chapitre.
            Ces blocs sont indépendants du texte source du Guide.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="guide-editor-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-refuge-ruby text-white font-body text-sm disabled:opacity-60 shrink-0"
        >
          <Save size={14} />
          {saving ? "Enregistrement…" : "Enregistrer les blocs"}
        </button>
      </div>

      {msg && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-refuge-ruby/10 border border-refuge-ruby/30 text-sm text-refuge-ruby">
          {msg}
        </div>
      )}

      {/* Sélecteur de chapitre — puces horizontales */}
      <div className="mb-6 flex flex-wrap gap-2">
        {CHAPTERS.map((c) => (
          <button
            key={c.slug}
            type="button"
            onClick={() => setChapter(c.slug)}
            data-testid={`chapter-tab-${c.slug}`}
            className={
              "px-3.5 py-1.5 rounded-full font-body text-xs transition-all border " +
              (chapter === c.slug
                ? "bg-refuge-ruby text-white border-refuge-ruby"
                : "bg-white text-refuge-soft border-refuge-border hover:border-refuge-ruby hover:text-refuge-ruby")
            }
          >
            {c.label}
            {(content?.guideExtras?.[c.slug]?.length || 0) > 0 && (
              <span className="ml-1.5 text-[10px] opacity-80">
                · {content.guideExtras[c.slug].length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Liste des blocs pour le chapitre courant */}
      <div className="space-y-3 mb-6">
        {extras.length === 0 && (
          <div className="text-center py-8 bg-refuge-bg rounded-xl border border-dashed border-refuge-border">
            <Sparkles size={18} className="mx-auto mb-2 text-refuge-soft" strokeWidth={1.3} />
            <p className="font-body text-sm text-refuge-soft italic">
              Aucun bloc personnalisé pour ce chapitre.
            </p>
            <p className="font-body text-xs text-refuge-soft mt-1">
              Utilise les boutons ci-dessous pour en ajouter.
            </p>
          </div>
        )}
        {extras.map((block, idx) => (
          <BlockCard
            key={idx}
            block={block}
            idx={idx}
            total={extras.length}
            uploaded={uploaded}
            onUpdate={(patch) => updateBlock(idx, patch)}
            onRemove={() => removeBlock(idx)}
            onMove={(delta) => moveBlock(idx, delta)}
            openPicker={openPicker}
          />
        ))}
      </div>

      {/* Boutons "ajouter un bloc" */}
      <div>
        <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-2">
          Ajouter un bloc
        </p>
        <div className="flex flex-wrap gap-2">
          {BLOCK_TYPES.map((bt) => (
            <button
              key={bt.type}
              type="button"
              onClick={() => addBlock(bt)}
              data-testid={`add-block-${bt.type}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-refuge-border bg-white text-refuge-text font-body text-sm hover:border-refuge-ruby hover:text-refuge-ruby transition-all"
              title={bt.hint}
            >
              <Plus size={13} strokeWidth={1.6} />
              {bt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Picker modal */}
      {pickerFor && (
        <MediaPicker
          uploaded={uploaded}
          onPick={(filename) => { pickerFor.onPick(filename); closePicker(); }}
          onClose={closePicker}
          onUploaded={async () => { setUploaded(await api.listMedia(site)); }}
          site={site}
        />
      )}
    </div>
  );
}

// ============================================================
// Un "bloc" éditable
// ============================================================
function BlockCard({ block, idx, total, uploaded, onUpdate, onRemove, onMove, openPicker }) {
  return (
    <div className="bg-white border border-refuge-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-refuge-border/60 bg-refuge-bg/60">
        <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby">
          {idx + 1} · {blockTypeLabel(block.type)}
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={idx === 0}
            className="p-1.5 text-refuge-soft hover:text-refuge-ruby disabled:opacity-30"
            title="Monter"
          >
            <MoveUp size={13} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={idx === total - 1}
            className="p-1.5 text-refuge-soft hover:text-refuge-ruby disabled:opacity-30"
            title="Descendre"
          >
            <MoveDown size={13} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 text-refuge-soft hover:text-refuge-ruby"
            title="Supprimer"
            data-testid={`block-remove-${idx}`}
          >
            <Trash2 size={13} strokeWidth={1.6} />
          </button>
        </div>
      </div>
      <div className="p-4">
        {block.type === "photo-caption" && (
          <PhotoCaptionEditor block={block} uploaded={uploaded} onUpdate={onUpdate} openPicker={openPicker} />
        )}
        {block.type === "callout" && (
          <CalloutEditor block={block} uploaded={uploaded} onUpdate={onUpdate} openPicker={openPicker} />
        )}
        {block.type === "gallery" && (
          <GalleryEditor block={block} uploaded={uploaded} onUpdate={onUpdate} openPicker={openPicker} />
        )}
      </div>
    </div>
  );
}

function blockTypeLabel(type) {
  const bt = BLOCK_TYPES.find((b) => b.type === type);
  return bt ? bt.label : type;
}

// ============================================================
// Éditeurs par type
// ============================================================
function PhotoCaptionEditor({ block, onUpdate, openPicker }) {
  return (
    <div className="space-y-3">
      <FieldPhoto
        label="Photo"
        value={block.src}
        onChange={(src) => onUpdate({ src })}
        openPicker={openPicker}
      />
      <FieldText label="Description (alt)" value={block.alt} onChange={(alt) => onUpdate({ alt })} />
      <FieldText label="Légende" value={block.caption} onChange={(caption) => onUpdate({ caption })} />
      <FieldText label="Sous-légende (facultatif)" value={block.subCaption} onChange={(subCaption) => onUpdate({ subCaption })} />
    </div>
  );
}

function CalloutEditor({ block, onUpdate, openPicker }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-1.5">
          Icône
        </label>
        <select
          value={block.icon || "Sparkles"}
          onChange={(e) => onUpdate({ icon: e.target.value })}
          className="w-full px-3 py-2 border border-refuge-border rounded-lg font-body text-sm"
        >
          {ICON_CHOICES.map((ic) => (
            <option key={ic} value={ic}>{ic}</option>
          ))}
        </select>
      </div>
      <FieldText label="Titre en majuscules" value={block.label} onChange={(label) => onUpdate({ label })} />
      <FieldTextarea label="Texte (**gras** supporté)" value={block.value} onChange={(value) => onUpdate({ value })} />
      <FieldPhoto
        label="Image (facultatif)"
        value={block.image}
        onChange={(image) => onUpdate({ image })}
        openPicker={openPicker}
        optional
      />
    </div>
  );
}

function GalleryEditor({ block, onUpdate, openPicker }) {
  const photos = block.photos || [];
  const addPhoto = () => onUpdate({ photos: [...photos, { src: "", alt: "", caption: "" }] });
  const setPhoto = (i, patch) =>
    onUpdate({ photos: photos.map((p, j) => (i === j ? { ...p, ...patch } : p)) });
  const removePhoto = (i) => onUpdate({ photos: photos.filter((_, j) => j !== i) });

  return (
    <div className="space-y-3">
      <FieldText label="Sur-titre (facultatif)" value={block.eyebrow} onChange={(eyebrow) => onUpdate({ eyebrow })} />
      <FieldText label="Titre (facultatif)" value={block.heading} onChange={(heading) => onUpdate({ heading })} />
      <div>
        <label className="block font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-2">
          Photos ({photos.length})
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {photos.map((p, i) => (
            <div key={i} className="border border-refuge-border rounded-lg p-3 bg-refuge-bg/40">
              <FieldPhoto
                label={`Photo ${i + 1}`}
                value={p.src}
                onChange={(src) => setPhoto(i, { src })}
                openPicker={openPicker}
                compact
              />
              <FieldText
                label="Légende"
                value={p.caption}
                onChange={(caption) => setPhoto(i, { caption })}
                compact
              />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="mt-2 inline-flex items-center gap-1 text-refuge-ruby text-[11px] font-body hover:underline"
              >
                <Trash2 size={11} /> Retirer
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addPhoto}
          data-testid="gallery-add-photo"
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-refuge-border text-refuge-ruby text-xs hover:border-refuge-ruby transition-all"
        >
          <Plus size={12} /> Ajouter une photo
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Champs génériques
// ============================================================
function FieldText({ label, value, onChange, compact }) {
  return (
    <div>
      <label className="block font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-1.5">
        {label}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 border border-refuge-border rounded-lg font-body text-sm ${compact ? "py-1.5" : "py-2"}`}
      />
    </div>
  );
}

function FieldTextarea({ label, value, onChange }) {
  return (
    <div>
      <label className="block font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-1.5">
        {label}
      </label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 border border-refuge-border rounded-lg font-body text-sm"
      />
    </div>
  );
}

function FieldPhoto({ label, value, onChange, openPicker, compact, optional }) {
  const filename = value ? value.split("/").pop() : "";
  const url = value
    ? (value.startsWith("http") ? value : `${BACKEND}/api/media/${encodeURIComponent(filename)}?v=${Date.now()}`)
    : null;
  return (
    <div>
      <label className="block font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-1.5">
        {label} {optional && <span className="text-refuge-soft/60 normal-case">(facultatif)</span>}
      </label>
      <div className={`flex items-center gap-3 ${compact ? "" : ""}`}>
        <div
          className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-refuge-bg border border-refuge-border flex items-center justify-center"
        >
          {url ? (
            <img src={url} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={16} className="text-refuge-soft" strokeWidth={1.4} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {value && (
            <p className="font-mono text-[10px] text-refuge-soft truncate mb-1" title={filename}>
              {filename}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => openPicker((f) => onChange(`/${f}`))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-refuge-border text-refuge-ruby text-[11px] font-body hover:border-refuge-ruby transition-all"
            >
              <ImageIcon size={11} /> Choisir…
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-refuge-soft text-[11px] hover:text-refuge-ruby transition-colors"
              >
                <X size={11} /> Retirer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Modal picker de médias — liste des uploads + upload libre
// ============================================================
function MediaPicker({ uploaded, onPick, onClose, onUploaded, site }) {
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const filtered = useMemo(
    () => uploaded.filter((f) => f.filename.toLowerCase().includes(q.toLowerCase())),
    [uploaded, q]
  );

  const uploadFree = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      // Nom auto : "custom-YYYYMMDD-HHMMSS-<slug>.<ext>"
      const clean = file.name
        .toLowerCase()
        .replace(/\.[^.]+$/, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40);
      const ext = file.name.split(".").pop() || "bin";
      const ts = new Date().toISOString().replace(/[:T-]/g, "").slice(0, 14);
      const filename = `custom-${ts}-${clean || "media"}.${ext.toLowerCase()}`;
      await api.uploadMedia(file, filename, site);
      await onUploaded();
      onPick(filename);
    } catch (er) {
      setErr(er?.response?.data?.detail || er.message || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid="media-picker"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[86vh] bg-white rounded-2xl overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-refuge-border">
          <p className="font-heading text-lg font-light text-refuge-text">Choisir un média</p>
          <button type="button" onClick={onClose} className="p-1.5 text-refuge-soft hover:text-refuge-ruby">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-refuge-border/60 flex gap-2">
          <input
            type="text"
            placeholder="Rechercher un fichier…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 px-3 py-2 border border-refuge-border rounded-lg text-sm font-body"
          />
          <label
            className={
              "inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs font-body cursor-pointer shrink-0 " +
              (uploading ? "bg-refuge-ruby/60 cursor-wait" : "bg-refuge-ruby hover:bg-refuge-ruby/90")
            }
            data-testid="picker-upload-free"
          >
            <Upload size={12} />
            {uploading ? "Upload…" : "Ajouter un fichier"}
            <input type="file" accept="image/*" onChange={uploadFree} className="hidden" disabled={uploading} />
          </label>
        </div>
        {err && <div className="px-5 py-2 text-refuge-ruby text-sm">{err}</div>}
        <div className="p-4 overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="text-center py-10 text-refuge-soft italic text-sm">
              Aucun média trouvé. Utilise « Ajouter un fichier » pour uploader une nouvelle image.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((f) => {
                const src = `${BACKEND}/api/media/${encodeURIComponent(f.filename)}?v=${encodeURIComponent(f.uploadDate || "")}`;
                return (
                  <button
                    type="button"
                    key={f.id || f.filename}
                    onClick={() => onPick(f.filename)}
                    data-testid={`picker-item-${f.filename}`}
                    className="group rounded-lg overflow-hidden border border-refuge-border hover:border-refuge-ruby text-left transition-all"
                  >
                    <div className="aspect-square bg-refuge-bg overflow-hidden">
                      <img src={src} alt={f.filename} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <p className="px-2 py-1.5 font-mono text-[9px] text-refuge-soft truncate" title={f.filename}>
                      {f.filename}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
